import React from 'react';
import { observer } from 'mobx-react-lite';

import { useStore } from '@vromo/mobx-react';
import orderBy from 'lodash/orderBy';

import Table from '@vromo/ui/components/Table';

import { RootStore } from '../stores/RootStore';
import { Blueprint } from '@vromo/models';
import BlueprintEditor from './BlueprintEditor';
import { ProfilePlaceholder } from '@vromo/ui/components/ProfileImage';
import { InnerContainer } from '../policies/components/styled';
import Flex from '@vromo/ui/components/Flex';
import LoadingIndicator from '@vromo/ui/components/LoadingIndicator';
import { useTranslation } from 'react-i18next';

interface BlueprintsProps {
    blueprintEditing: boolean;
    editedBlueprint: Blueprint;
    blueprintOnClick: (blueprint: Blueprint) => void;
    blueprintOnClickClose: () => void;
}

export const BlueprintRow = observer(function BlueprintRow({
    onClick,
    blueprint
}: {
    onClick: () => void;
    blueprint: Blueprint;
}) {
    return (
        <tr onClick={onClick} style={{ cursor: 'pointer' }}>
            <td style={{ textAlign: 'center', width: 50 }}>
                <ProfilePlaceholder initials={blueprint.name[0]} id={blueprint.id} />
            </td>
            <td>{blueprint.name}</td>
            <td>{blueprint.id}</td>
        </tr>
    );
});

export default observer(function Blueprints({
    blueprintEditing,
    editedBlueprint,
    blueprintOnClick,
    blueprintOnClickClose
}: BlueprintsProps) {
    const { t } = useTranslation();
    const { blueprintStore } = useStore<RootStore>('rootStore');

    return (
        <>
            <InnerContainer>
                {blueprintStore.loaded ? (
                    <Table>
                        <thead>
                            <tr>
                                <th />
                                <th>{t('blueprint.name')}</th>
                                <th>{t('blueprint.id')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orderBy(
                                blueprintStore.blueprints.items,
                                (it) => it.name.trim().toLowerCase(),
                                ['asc']
                            ).map((blueprint: Blueprint) => (
                                <BlueprintRow
                                    key={blueprint.id}
                                    onClick={() => blueprintOnClick(blueprint)}
                                    blueprint={blueprint}
                                />
                            ))}
                        </tbody>
                    </Table>
                ) : (
                    <Flex>
                        {t('blueprint.loading')} <LoadingIndicator />
                    </Flex>
                )}
            </InnerContainer>
            <BlueprintEditor
                open={blueprintEditing}
                close={() => {
                    blueprintOnClickClose();
                }}
                blueprint={editedBlueprint}
                isNew={editedBlueprint.id === undefined}
            />
        </>
    );
});
